import { Radio, Col, Row, message } from 'antd';
import config from 'config';
import { Routes } from 'config/routes';
import {
  PermissionsEnum,
  UserListSort,
  useGetOwnersListQuery,
  UserOutput,
  OwnersListFilterInput,
  useGetLocationsQuery,
} from 'gql/types';
import { Button, Spin, TableSearchInput } from 'modules/common/components';
import { FilterChips } from 'modules/common/components/FilterChips';
import Link from 'modules/common/components/Link';
import { useSort, useDebounce, useFilter } from 'modules/common/hooks';
import { useRegionalFilterInit } from 'modules/common/hooks/useRegionalFilterInit';
import ProfileSideMenuWrapper from 'modules/navigation/components/ProfileSideMenuWrapper';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import OwnerListTable from 'modules/owner/components/OwnerListTable';
import {
  createOwnerListChipsConfig,
  excludedFilterFields,
} from 'modules/owner/configs/ownerListFilters';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { useUserPermissionsAndRegion } from 'modules/user/hooks/useUserPermissionsAndRegion';
import { FC, useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

enum DonorStatus {
  Active,
  Deleted,
}

const OwnerListPage: FC = () => {
  const { t } = useTranslation('owner.OwnerListPage');

  const [deletedSwitchValue, setDeletedSwitchValue] = useState(
    DonorStatus.Active
  );
  const setDeletedRadio = useRef(false);

  const currentUser = useCurrentUser();
  const isUpdateOwnersDataAllowed = currentUser.isCan(
    PermissionsEnum.UpdateOwnersData
  );
  const isAddUserAllowed = currentUser.isCan(PermissionsEnum.AddUsers);

  const pagination = usePagination();
  const sort = useSort<UserListSort>();
  const filter = useFilter<OwnersListFilterInput>();

  const {
    assignedLocations,
    canViewAllRegions,
    hasLocationRestrictions,
    isAdmin,
  } = useUserPermissionsAndRegion();

  const assignedLocationNames = useMemo(
    () => assignedLocations.map((l) => l.name),
    [assignedLocations]
  );

  const { isLoading: isRegionalFilterLoading } =
    useRegionalFilterInit<OwnersListFilterInput>({
      filterHook: filter,
      filterFieldName: 'locationPreferences',
      canViewAllRegions,
      hasLocationRestrictions,
      assignedLocationNames,
      successMessage: t('regionalFilterApplied'),
    });

  const { data: allAvailableLocationsData } = useGetLocationsQuery({
    variables: {
      input: {
        skip: 0,
        take: 1000,
      },
    },
  });

  const locationFilterOptions =
    allAvailableLocationsData?.getLocations.data.map((location) => ({
      text: location.name,
      value: location.name,
    }));

  const chipsConfig = useMemo(() => createOwnerListChipsConfig(t), [t]);

  const ownersFilters = filter.getFilterObject();

  const getUsersListQuery = useGetOwnersListQuery({
    variables: {
      input: {
        filter: ownersFilters,
        take: config.PAGINATION.DEFAULT_LIMIT,
        skip: pagination.skip,
        sort: sort.options,
      },
    },
    skip: isRegionalFilterLoading,
    onCompleted: (data) => {
      pagination.setTotal(data.getOwnersList.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const ownerList = getUsersListQuery.data?.getOwnersList.data as UserOutput[];
  const ownerListConfig = {
    pagination,
    ownerList,
    sorting: sort,
  };

  const { debounce } = useDebounce();
  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    filter.setFilterOption({
      fieldName: 'searchingQuery',
      value,
    });
  };

  const handleDeletedUsersFilter = (value: number) => {
    const showDeletedRadioValue = value === DonorStatus.Deleted;
    const switchPosition = showDeletedRadioValue
      ? DonorStatus.Deleted
      : DonorStatus.Active;
    setDeletedSwitchValue(switchPosition);
    filter.setFilterOption({
      fieldName: 'isDeleted',
      value: showDeletedRadioValue,
    });
  };

  const showDeletedColumn =
    deletedSwitchValue === DonorStatus.Deleted && isAdmin;

  useEffect(() => {
    if (!setDeletedRadio.current && ownersFilters?.isDeleted) {
      handleDeletedUsersFilter(DonorStatus.Deleted);
      setDeletedRadio.current = true;
    }
  }, [ownersFilters]);

  const isLoading = isRegionalFilterLoading || getUsersListQuery.loading;

  return (
    <ProfileSideMenuWrapper title={t('title')}>
      <Spin spinning={isLoading}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={[16, 10]} justify="space-between" align="middle">
              <Col flex={1}>
                <TableSearchInput
                  onSearchChange={debounce(handleSearch)}
                  placeholder={t('searchOwner')}
                  maxLength={100}
                />
              </Col>
              {isAdmin && (
                <Col>
                  <Radio.Group
                    defaultValue={DonorStatus.Active}
                    onChange={(e) => handleDeletedUsersFilter(e.target.value)}
                    value={deletedSwitchValue}
                  >
                    <Radio.Button value={DonorStatus.Active}>
                      {t('active')}
                    </Radio.Button>
                    <Radio.Button value={DonorStatus.Deleted}>
                      {t('deleted')}
                    </Radio.Button>
                  </Radio.Group>
                </Col>
              )}
              {isUpdateOwnersDataAllowed && (
                <Col>
                  <Button type="primary" disabled={!isAddUserAllowed}>
                    <Link to={Routes.CreateOwner}>{t('addNewOwner')}</Link>
                  </Button>
                </Col>
              )}
            </Row>
          </Col>

          <Col span={24}>
            <FilterChips<OwnersListFilterInput>
              filterHook={filter}
              config={chipsConfig}
              excludeFields={excludedFilterFields}
            />
          </Col>

          <Col span={24}>
            <OwnerListTable
              {...ownerListConfig}
              showDeletedColumn={showDeletedColumn}
              ownersFilters={filter}
              locationFilterOptions={locationFilterOptions}
            />
          </Col>
        </Row>
      </Spin>
    </ProfileSideMenuWrapper>
  );
};

export default OwnerListPage;
